import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ai2Layout } from '../layout/Ai2Layout';
import { PatientEndlessListTable } from '../components/Table/PatientEndlessListTable';
import { useEndlessScrollPatientList } from '../components/Table/hooks/useEndlessScrollPatientList';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import { SystemPermission, useTriggerUpdate } from '../../context';
import { useGlobalLoading } from '../../context';
import DashboardSavedSearchFilters from './Dashboard/DashboardSavedSearchFilters';
import { resetCurrentFilter } from '../../store/ai2';
import useUserPermission from '../hooks/useUserPermission';
import { useScrollLoadMore } from '../components/Table/hooks/useScrollLoadMore';
import { FilterComponent } from '../components/Filter/filter';
import AishFilters from '../components/Filter/AishFilters';
import PermissionMissing from '../../auth/pages/PermissionMissing';

const Dashboard = () => {
  const { userHasPermissions } = useUserPermission();
  const navigate = useNavigate();
  const { fromNavBar } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );

  const dispatch = useDispatch();
  const {
    startLoading,
    stopLoading,
    isLoading: isUpdatingList,
  } = useGlobalLoading();
  const { updatePatientListCount, resetHaveMoreData, haveMoreData } =
    useTriggerUpdate();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  /* Future case if we want to check if the dashboard menu item is available
  const dashboardMenuItem = useMemo(() => {
    if (!selectedTenancy) return [];
    const dashboardTopBarSettings =
      selectedTenancy?.features?.dashboardTopBarSettings; 
    if (
      dashboardTopBarSettings?.menuItems &&
      Array.isArray(dashboardTopBarSettings.menuItems)
    ) {
      // deep copy as sort will affect original array
      const menuItems = JSON.parse(
        JSON.stringify(dashboardTopBarSettings.menuItems),
      );
      return menuItems.find((item: any) => item && item.id === 'dashboard');
    }
    return [];
  }, [selectedTenancy]);*/

  const { filterState, sortState } = useSelector(
    (state: RootState) => state.patientList,
  );

  const { currentFilters, userSavedFilters, savedFiltersInitialized } =
    useSelector((state: RootState) => state.dashboardSearch);

  const hasSearchFeature = !!selectedTenancy?.features?.searchFiltersMap;
  const savedSearchDisabled =
    selectedTenancy?.features?.searchScreen?.savedSearchDisabled ?? false;

  const cannotGoToDashboard =
    !savedSearchDisabled &&
    currentFilters.length === 0 &&
    userSavedFilters.length === 0 &&
    savedFiltersInitialized &&
    hasSearchFeature;

  const userPinnedFilter = (
    userSavedFilters.find((item) => item.pinned)?.text ||
    userSavedFilters[0]?.text ||
    ''
  )
    .split(',')
    .map((filter) => filter.trim())
    .filter(Boolean);

  const theMostUsedFilter = (userSavedFilters[0]?.text || '')
    .split(',')
    .map((filter) => filter.trim())
    .filter(Boolean);

  const getSearchFiltersArg = () => {
    if (!hasSearchFeature) return undefined;
    if (savedSearchDisabled) return undefined;
    if (fromNavBar) return userPinnedFilter;
    if (currentFilters.length > 0) return currentFilters;
    return theMostUsedFilter;
  };

  const {
    currentPage,
    setCurrentPage,
    getPatientList,
    isLoading,
    response,
    timeoutError,
    resetResponse,
    setIsLoading,
  } = useEndlessScrollPatientList(30);

  useEffect(() => {
    if (!selectedTenancy) return;
    resetResponse();
    setCurrentPage(1);
    resetHaveMoreData(true);
    if (!hasSearchFeature) {
      dispatch(resetCurrentFilter());
    }
  }, [selectedTenancy?.tenancy_id]);

  useEffect(() => {
    if (!selectedTenancy) return;
    resetHaveMoreData(true);
    // put loading status and request in the end of queue to prevent abort race
    setTimeout(() => {
      setIsLoading(true);
      const searchFiltersArg = getSearchFiltersArg();

      getPatientList(
        currentPage,
        sortState,
        filterState,
        selectedTenancy,
        searchFiltersArg,
      );
    }, 0);
  }, [
    currentPage,
    selectedTenancy,
    filterState,
    sortState,
    currentFilters,
    fromNavBar,
    hasSearchFeature,
  ]);

  useEffect(() => {
    return () => {
      resetHaveMoreData(true);
      resetResponse();
      setCurrentPage(1);
    };
  }, []);

  // update patient list when sorting/filters changes
  useEffect(() => {
    setCurrentPage(1);
    resetHaveMoreData(true);
  }, [sortState, filterState, currentFilters]);

  // update patient list when updatePatientListCount changes
  useEffect(() => {
    if (!selectedTenancy) return;
    if (updatePatientListCount > 0) {
      setIsLoading(true);
      const searchFiltersArg = getSearchFiltersArg();

      getPatientList(
        1,
        sortState,
        filterState,
        selectedTenancy,
        searchFiltersArg,
      );
    }
  }, [updatePatientListCount]);

  useEffect(() => {
    if (isLoading) {
      startLoading();
    } else {
      stopLoading();
    }
  }, [isLoading]);

  useEffect(() => {
    if (cannotGoToDashboard) {
      navigate('/search');
    }
  }, [cannotGoToDashboard]);

  const onLoadMore = () => {
    if (isLoading) return;

    setIsLoading(true);
    setCurrentPage((currentPage || 1) + 1);
  };

  useScrollLoadMore('.ui.offsetNavbar', onLoadMore, !haveMoreData);

  const tenancyFilter: React.ReactNode = useMemo(() => {
    if (!selectedTenancy) {
      return <></>;
    }

    const isAishFilter =
      selectedTenancy.features?.dashboardFilters?.type == 'aish';

    if (isAishFilter) {
      return <AishFilters />;
    }
    return <FilterComponent />;
  }, [selectedTenancy]);

  // Show loading or empty state while navigation is happening
  if (
    selectedTenancy &&
    selectedTenancy?.permissionsSetup &&
    !userHasPermissions(SystemPermission.VIEW_PATIENT_LIST)
  ) {
    return (
      <Ai2Layout>
        <PermissionMissing />
      </Ai2Layout>
    );
  }

  return (
    <Ai2Layout>
      <div
        className="ui offsetNavbar"
        style={{ maxHeight: '100vh', overflow: 'auto', paddingBottom: '150px' }}
      >
        {/* {shouldShowSavedSearchFilters ? (
          <AutomedicDashboardBanner />
        ) : (
          <div>
            <FilterComponent />
          </div>
        )} */}
        {!hasSearchFeature ? (
          <div>{tenancyFilter}</div>
        ) : (
          <DashboardSavedSearchFilters />
        )}
        <div className="dashboard-layout">
          {/* This is original pagination table as reference */}
          {/* <PatientListTable /> */}
          <PatientEndlessListTable
            data={response}
            loading={isLoading || isUpdatingList}
            timeoutError={timeoutError}
            currentPage={currentPage}
            filterState={filterState}
            sortState={sortState}
            selectedTenancy={selectedTenancy}
          />
        </div>
      </div>
    </Ai2Layout>
  );
};

export { Dashboard };
